import { createContext, useContext, useEffect, useRef } from 'react';
import type { OpcionContexto } from '../componentes/Contexto';
import type { NombreIcono } from '../componentes/iconos';

/**
 * Lo que una pantalla sube a la línea del título del marco (componentes.md §1b: «Lo pinta 0.1, no la sección»): sus
 * tarjetas de la «?» (§3, toda explicación de la pantalla va ahí) y su botón principal (normalmente uno, el del apartado
 * activo). En ordenador van a la derecha de la cabecera en curva, en este orden: «?» · acciones; en móvil, la «?» en la
 * barra de arriba junto a la campana y el botón principal, a todo el ancho, encima del contenido.
 */
export interface TarjetaAyuda {
    icono: NombreIcono;
    titulo: string;
    texto: string;
}

export interface AccionCabecera {
    /** Clave estable (para no volver a subirla si no cambia). */
    clave: string;
    texto: string;
    icono: NombreIcono;
    onClick: () => void;
    desactivado?: boolean;
    /** Secundaria (blanca con borde) en lugar de la principal roja. */
    secundaria?: boolean;
}

/**
 * Selector de contexto de la pantalla (componentes.md §1b y §4d, G814): lo que elige qué estás mirando (evento y jornada
 * en Puerta…). En ordenador lo pinta el marco con RV Contexto en la línea del título, entre la «?» y el botón principal;
 * en móvil la pantalla lo pinta en su sitio con la misma pieza.
 */
export interface ContextoDeCabecera {
    /** Clave estable (para no volver a subirlo si no cambia). */
    clave: string;
    etiqueta: string;
    valor: string;
    opciones: readonly OpcionContexto[];
    icono: NombreIcono;
    onChange: (valor: string) => void;
}

export interface Herramientas {
    ayuda: readonly TarjetaAyuda[];
    acciones: readonly AccionCabecera[];
    /** Selectores de contexto de la pantalla, en la línea del título (G814); solo en ordenador. */
    contexto?: readonly ContextoDeCabecera[];
    /**
     * Título propio de la pantalla en lugar de la etiqueta de su entrada del menú (p. ej. «Mi agenda» del Profesional en la
     * entrada «Agenda», G779). Sin él, el de siempre.
     */
    titulo?: string;
    /**
     * Línea corta bajo el título (0.1, «título y subtítulo»), solo en ordenador. Si nombra lo que se reserva, va con la
     * palabra del negocio (G214: «reservados» en ocio nocturno): ver `conVocabulario`.
     */
    subtitulo?: string;
}

/** Plural de la palabra del negocio: «mesa» → «mesas», «reservado» → «reservados», «habitación» → «habitaciones». */
export function plural(palabra: string): string {
    const p = palabra.trim();
    if (p === '' || /s$/i.test(p)) {
        return p;
    }
    if (/ión$/i.test(p)) {
        return `${p.slice(0, -3)}iones`;
    }
    return /[aeiouáéó]$/i.test(p) ? `${p}s` : `${p}es`;
}

/**
 * El texto de la cabecera con la palabra del negocio (G214): `{recurso}` y `{recursos}` se cambian por la palabra del
 * vocabulario (la del negocio o, si no la cambió, la de su sector) en singular o plural; nunca «mesa» fija.
 */
export function conVocabulario(texto: string, palabra: string): string {
    const p = palabra.trim().toLocaleLowerCase('es') || 'recurso';
    return texto.replace(/\{recursos\}/g, plural(p)).replace(/\{recurso\}/g, p);
}

export const SIN_HERRAMIENTAS: Herramientas = { ayuda: [], acciones: [] };

export const ContextoCabecera = createContext<(h: Herramientas | null) => void>(() => undefined);

/**
 * Sube a la cabecera la «?» y las acciones de la pantalla mientras está abierta. Solo vuelve a subirlas si cambia lo que
 * se ve (textos, iconos, desactivado); los `onClick` siempre llaman a la versión más reciente.
 */
export function useCabecera(h: Herramientas): void {
    const fijar = useContext(ContextoCabecera);
    const ultimo = useRef(h);
    useEffect(() => {
        ultimo.current = h;
    });
    const firma = JSON.stringify({
        titulo: h.titulo,
        subtitulo: h.subtitulo,
        ayuda: h.ayuda,
        acciones: h.acciones.map((a) => ({ clave: a.clave, texto: a.texto, icono: a.icono, desactivado: a.desactivado, secundaria: a.secundaria })),
        contexto: h.contexto?.map((c) => ({ clave: c.clave, etiqueta: c.etiqueta, valor: c.valor, opciones: c.opciones, icono: c.icono })),
    });
    useEffect(() => {
        const visto = JSON.parse(firma) as { titulo?: string; subtitulo?: string; ayuda: TarjetaAyuda[]; acciones: Omit<AccionCabecera, 'onClick'>[]; contexto?: Omit<ContextoDeCabecera, 'onChange'>[] };
        fijar({
            ...(visto.titulo === undefined ? {} : { titulo: visto.titulo }),
            ...(visto.subtitulo === undefined ? {} : { subtitulo: visto.subtitulo }),
            ...(visto.contexto === undefined || visto.contexto.length === 0
                ? {}
                : {
                      contexto: visto.contexto.map((c) => ({
                          ...c,
                          onChange: (valor: string) => {
                              ultimo.current.contexto?.find((x) => x.clave === c.clave)?.onChange(valor);
                          },
                      })),
                  }),
            ayuda: visto.ayuda,
            acciones: visto.acciones.map((a) => ({
                ...a,
                onClick: () => {
                    ultimo.current.acciones.find((x) => x.clave === a.clave)?.onClick();
                },
            })),
        });
    }, [firma, fijar]);
    useEffect(
        () => () => {
            fijar(null);
        },
        [fijar],
    );
}
